// Thin wrapper around the shared useCatalogCart (see
// src/features/CatalogGrid/hooks/useCatalogCart.js) — supplies
// PurchaseLinkSlice's selectors/action instead of ProposalSlice's (compare
// ProposalTool's useProductsCatalog). Every account reached via "Create
// Purchase Link" already exists in Recurly, so unlike the Proposal Tool
// there's no NEW/EXISTING clientType distinction (always 'EXISTING') and no
// email-lookup recovery effect — the active location's Recurly info is
// fetched directly by usePurchaseLinkLocation, keyed by its own thryvId.
import {useCallback, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useCatalogCart} from '../../../../../features/CatalogGrid/hooks/useCatalogCart'
import {useUserFeatures} from '../../../../../context/user-features-context'
import {
  selectActiveLocation,
  selectActiveLocationCountry,
  selectActiveLocationRecurlyInfo,
  selectActiveLocationRecurlySubscriptionItems,
  selectAddonGroups,
  selectIsCatalogDataLoaded,
  selectOneTimeFeeDefinitions,
  selectProductConfigInfo,
  selectSoftwareGroups,
  setBniMktgSvcsEnabled,
  updateLocation,
} from '../../../../../app-store/PurchaseLinkSlice'

export function usePurchaseLinkProducts() {
  const dispatch = useDispatch()
  const userFeatures = useUserFeatures()

  const activeLocation = useSelector(selectActiveLocation)
  const selectedCountry = useSelector(selectActiveLocationCountry)
  const recurlyAccountInfo = useSelector(selectActiveLocationRecurlyInfo)
  const rawAccountItems = useSelector(
    selectActiveLocationRecurlySubscriptionItems,
  )
  const rawSoftwareGroups = useSelector(selectSoftwareGroups)
  const rawAddonGroups = useSelector(selectAddonGroups)
  const isDataLoaded = useSelector(selectIsCatalogDataLoaded)
  const oneTimeFeeDefinitions = useSelector(selectOneTimeFeeDefinitions)
  const productConfigInfo = useSelector(selectProductConfigInfo)

  // Enable_BNI_Mktg_Svcs is needed inside a plain Redux reducer
  // (buildItemsFromAccountSubscriptions, dispatched from
  // setRecurlyAccountInfoForLocation), which has no access to this React
  // context — mirror the flag into Redux so that reducer can read it.
  useEffect(() => {
    dispatch(setBniMktgSvcsEnabled(Boolean(userFeatures?.Enable_BNI_Mktg_Svcs)))
  }, [dispatch, userFeatures?.Enable_BNI_Mktg_Svcs])

  const onItemsChange = useCallback(
    items => {
      if (!activeLocation) return
      dispatch(
        updateLocation({
          locationId: activeLocation.locationId,
          changes: {items},
        }),
      )
    },
    [dispatch, activeLocation],
  )

  return useCatalogCart({
    activeItem: activeLocation,
    country: selectedCountry,
    recurlyAccountInfo,
    rawAccountItems,
    rawSoftwareGroups,
    rawAddonGroups,
    isDataLoaded,
    oneTimeFeeDefinitions,
    productConfigInfo,
    clientType: 'EXISTING',
    onItemsChange,
  })
}
